import { isAreaFeature, type EditorFeature } from '../models/editor-feature';
import { multiPolygonCentroid } from './rotation';
import { toScreen, type Projection } from './projection';

/**
 * The box drawn around the current selection, and the grips that resize and rotate it.
 *
 * Pure geometry, in surface coordinates, because two separate things need the same answer:
 * the painter has to draw the grips, and the pointer handling has to know when a press
 * landed on one. On the SVG renderer they were the same object and the browser reconciled
 * them; on a canvas they are not, and a grip drawn anywhere other than where it is picked is
 * a grip that cannot be grabbed.
 */

export interface SelectionBounds {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export const HANDLE_SIZE = 9;

/** How far the rotation grip sits above the box, and the stalk that connects it. */
export const ROTATE_HANDLE_OFFSET = 22;
export const ROTATE_HANDLE_RADIUS = 6;

/** How far the box sits outside the selection, so it frames rather than traces it. */
const PADDING = 10;

/**
 * The extent of a selection on screen.
 *
 * An area is framed by its vertices rather than its centre, so the box does not cut through
 * the drawing it is meant to contain.
 */
export const boundsOf = (
  features: readonly EditorFeature[],
  projection: Projection,
): SelectionBounds | undefined => {
  if (!features.length) {
    return undefined;
  }

  const points = features.map((feature) => toScreen(
    isAreaFeature(feature) ? multiPolygonCentroid(feature.coordinates) : feature.coordinates,
    projection,
  ));

  features.filter(isAreaFeature).forEach((feature) => {
    feature.coordinates.forEach((polygon) => polygon.forEach((ring) => ring.forEach((vertex) => {
      points.push(toScreen(vertex, projection));
    })));
  });

  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);

  const minX = Math.min(...xs) - PADDING;
  const minY = Math.min(...ys) - PADDING;

  return {
    x: minX,
    y: minY,
    width: Math.max(...xs) + PADDING - minX,
    height: Math.max(...ys) + PADDING - minY,
  };
};

export type HandleKind = 'resize' | 'rotate';

export interface Handle {
  readonly id: string;
  readonly kind: HandleKind;
  readonly x: number;
  readonly y: number;
  readonly cursor: string;
}

/**
 * Whether the selection has a size to change.
 *
 * Everything does now: a desk marker has no footprint, but its icon has a size, and dragging
 * the grip grows or shrinks it. Kept as its own question, apart from rotation, so the two grips
 * stay independently answerable.
 */
export const isResizable = (features: readonly EditorFeature[]): boolean => features.length > 0;

/**
 * Every grip on the selection box.
 *
 * All four corners are live rather than just one: which corner is nearest depends on where
 * the element sits and which way the plan is panned, and a single handle regularly ended up
 * the far side of the shape from the cursor.
 */
export const handlesOf = (bounds: SelectionBounds, resizable: boolean): Handle[] => {
  const handles: Handle[] = [{
    id: 'rotate',
    kind: 'rotate',
    x: bounds.x + bounds.width / 2,
    y: bounds.y - ROTATE_HANDLE_OFFSET,
    cursor: 'grab',
  }];

  if (!resizable) {
    return handles;
  }

  return handles.concat([
    {
      id: 'top-left', kind: 'resize', x: bounds.x, y: bounds.y, cursor: 'nwse-resize',
    },
    {
      id: 'top-right', kind: 'resize', x: bounds.x + bounds.width, y: bounds.y, cursor: 'nesw-resize',
    },
    {
      id: 'bottom-left', kind: 'resize', x: bounds.x, y: bounds.y + bounds.height, cursor: 'nesw-resize',
    },
    {
      id: 'bottom-right', kind: 'resize', x: bounds.x + bounds.width, y: bounds.y + bounds.height, cursor: 'nwse-resize',
    },
  ]);
};

/**
 * The grip under a point, if any.
 *
 * Grips are checked before features, so a corner handle sitting over a neighbouring desk
 * still resizes rather than selecting that desk — the same precedence the SVG overlay got
 * from being painted last.
 */
export const handleAt = (
  point: { x: number; y: number },
  bounds: SelectionBounds,
  resizable: boolean,
): Handle | undefined => handlesOf(bounds, resizable).find((handle) => {
  const reach = handle.kind === 'rotate' ? ROTATE_HANDLE_RADIUS + 2 : HANDLE_SIZE / 2 + 2;

  return Math.abs(point.x - handle.x) <= reach && Math.abs(point.y - handle.y) <= reach;
});
